import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DeviceProvider } from './contexts/DeviceContext';
import LoginPage from './components/LoginPage';
import ChildSelection from './components/ChildSelection';
import DeviceDetection from './components/DeviceDetection';
import FindMyInstructions from './components/FindMyInstructions';
import AppSelection from './components/AppSelection';
import SuccessPage from './components/SuccessPage';
import ConfirmationPage from './components/ConfirmationPage';
import MentorInviteInfo from './pages/MentorInviteInfo';
import Layout from './components/Layout';
import NotFound from "./pages/NotFound";
import { Loader } from 'lucide-react';

const LoadingScreen = () => {
  console.log('🔄 LoadingScreen displayed - Checking authentication status...');
  
  return (
    <div className="h-screen flex flex-col items-center justify-center bg-background">
      <div className="text-center space-y-6 max-w-md">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
          <Loader className="w-8 h-8 text-primary animate-spin" />
        </div>
        
        <div>
          <h2 className="text-xl font-bold text-foreground mb-2">Loading</h2>
          <p className="text-muted-foreground">
            Please wait while we check your authentication status...
          </p>
        </div>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  console.group('🛡️ Protected Route Check');
  console.log('Is Loading:', isLoading);
  console.log('Is Authenticated:', isAuthenticated);
  console.log('Action:', isLoading ? 'Show loading screen' : isAuthenticated ? 'Allow access' : 'Redirect to login');
  console.groupEnd();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const AppRoutes = () => {
  const { isLoading } = useAuth();
  
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/mentor-invite-info" element={<MentorInviteInfo />} />
      {/* Temporary preview routes - remove before production */}
      <Route path="/preview/success" element={<SuccessPage />} />
      <Route path="/preview/confirmation" element={
        <Layout>
          <ConfirmationPage />
        </Layout>
      } />
      <Route path="/preview/find-my-instructions" element={
        <Layout>
          <FindMyInstructions />
        </Layout>
      } />
      <Route path="/" element={
        <ProtectedRoute>
          <Layout>
            <ChildSelection />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/device-detection" element={
        <ProtectedRoute>
          <Layout>
            <DeviceDetection />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/find-my-instructions" element={
        <ProtectedRoute>
          <Layout>
            <FindMyInstructions />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/app-selection" element={
        <ProtectedRoute>
          <Layout>
            <AppSelection />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/success" element={
        <ProtectedRoute>
          <SuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/confirmation" element={
        <ProtectedRoute>
          <Layout>
            <ConfirmationPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <TooltipProvider>
    <Toaster />
    <Sonner />
    <BrowserRouter>
      <AuthProvider>
        <DeviceProvider>
          <AppRoutes />
        </DeviceProvider>
      </AuthProvider>
    </BrowserRouter>
  </TooltipProvider>
);

export default App;
